import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import Seo, { SITE } from '../../components/Seo/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faLocationDot,
	faCalendarDay,
	faClock,
	faUserGroup,
	faArrowRight,
	faCircleCheck,
	faArrowRotateLeft,
	faCircleQuestion,
} from '@fortawesome/pro-regular-svg-icons';

import { fetchWorkshops, workshopSeatsRemaining } from '../../utils/workshopService';
import { isDevPreview } from '../../utils/devPreview';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import summerHero from '../../assets/IMG_6438.jpeg';
import './Summer.css';
import './SummerLanding.css';

const MONTHS = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Month name from an ISO 'YYYY-MM-DD' (timezone-safe, no Date parsing drift).
function monthOf(iso) {
	if (!iso) return null;
	const m = Number(String(iso).split('-')[1]);
	return MONTHS[m - 1] || null;
}
function shortMonth(iso) {
	if (!iso) return '';
	const m = Number(String(iso).split('-')[1]);
	return MONTH_ABBR[m - 1] || '';
}
// Weekday range from a dateLabel like "June 15–18, 2026 (Mon–Thu)" → "Mon–Thu".
function weekdays(dateLabel) {
	const m = /\(([^)]+)\)/.exec(dateLabel || '');
	return m ? m[1] : '';
}
// "9:00 AM" → "9am"; "12:30 PM" → "12:30pm".
function shortTime(t) {
	if (!t) return '';
	const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(t).trim());
	if (!m) return t;
	const [, h, mm, ap] = m;
	return mm === '00' ? `${h}${ap.toLowerCase()}` : `${h}:${mm}${ap.toLowerCase()}`;
}
const timeSpan = (g) => `${shortTime(g.startTime)}–${shortTime(g.endTime)}`;

// Themed accent bar per workshop (design system gradients). Falls back to teal.
const ACCENT = {
	songwriting: 'var(--grad-custom)',
	allstate: 'var(--grad-summer)',
	acting: 'var(--grad-lessons)',
	mtaudition: 'var(--grad-littles)',
};
const accentFor = (theme) => ACCENT[theme] || 'var(--grad-summer)';

// Status pill driven by REAL seat counts (reuses workshopSeatsRemaining).
function seatPill(w) {
	if (w.status === 'draft') return { tone: 'soon', label: 'Draft' };
	if (w.status === 'closed') return { tone: 'closed', label: 'Closed' };
	const seats = workshopSeatsRemaining(w);
	if (seats <= 0) return { tone: 'full', label: 'Full' };
	if (seats <= 5) return { tone: 'low', label: `${seats} ${seats === 1 ? 'spot' : 'spots'} left` };
	return { tone: 'open', label: 'Open' };
}

// Instructor byline — Grace-first ordering comes from the data's instructors[]
// array (lead first); a 3rd+ name renders as a muted panel note.
function byline(instructors) {
	const list = instructors || [];
	if (!list.length) return null;
	if (list.length <= 2) return <>with {list.join(' & ')}</>;
	return (
		<>
			with {list[0]} <span className='wk__panel'>· {list.slice(1).join(' & ')}</span>
		</>
	);
}
// Time row(s): per-group when multiple groups, single span otherwise.
function timeLines(w) {
	const gs = w.groups || [];
	if (gs.length <= 1) {
		const g = gs[0] || {};
		const multiDay = w.startDate && w.endDate && w.startDate !== w.endDate;
		return [timeSpan(g) + (multiDay ? ' daily' : '')];
	}
	return gs.map((g) => (g.ageRange ? `Ages ${g.ageRange} · ` : `${g.label} · `) + timeSpan(g));
}
// Group / capacity / age summary row.
function capLine(w) {
	const gs = w.groups || [];
	if (gs.length <= 1) {
		const g = gs[0] || {};
		return [g.ageRange ? `Ages ${g.ageRange}` : null, g.capacity ? `cap ${g.capacity}` : null]
			.filter(Boolean)
			.join(' · ');
	}
	const caps = gs.map((g) => g.capacity);
	const same = caps.every((c) => c === caps[0]);
	return same ? `${gs.length} groups · cap ${caps[0]} each` : `${gs.length} groups`;
}

const SummerWorkshops = () => {
	const { userProfile } = useAuth();
	const [searchParams] = useSearchParams();
	const previewParam = searchParams.get('preview') === 'true';
	// Admin-only draft preview (Review 3 #1); isDevPreview keeps local dev working.
	const isAdminUser = userProfile?.role === 'admin';
	const includeDrafts = (previewParam && isAdminUser) || isDevPreview();
	const previewSuffix = previewParam ? '?preview=true' : '';

	const [workshops, setWorkshops] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [monthFilter, setMonthFilter] = useState('all');

	useEffect(() => {
		let active = true;
		(async () => {
			try {
				const ws = await fetchWorkshops({ includeDrafts });
				if (active) setWorkshops(ws);
			} catch (err) {
				console.error('Failed to load summer offerings:', err);
				if (active) setError(true);
			} finally {
				if (active) setLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [includeDrafts]);

	// Months present in the slate, calendar order (drives the jump-chips).
	const months = useMemo(() => {
		const present = new Set(workshops.map((w) => monthOf(w.startDate)).filter(Boolean));
		return MONTHS.filter((m) => present.has(m));
	}, [workshops]);

	const visible = useMemo(
		() =>
			monthFilter === 'all'
				? workshops
				: workshops.filter((w) => monthOf(w.startDate) === monthFilter),
		[workshops, monthFilter]
	);

	return (
		<main className='cyv summer-landing'>
			<Seo
				title='Summer 2026 | Color Your Voice'
				description='Summer 2026 at Color Your Voice — four group workshops for kids and teens in Burnsville, MN. Open to all families.'
				canonical={`${SITE}/summer`}
			/>

			{/* ---- Hero (full-bleed photo background + scrim, white overlay) ---- */}
			<header className='s-hero s-hero--photo'>
				<img
					className='s-hero__bg'
					src={summerHero}
					alt='Young artists at Color Your Voice inside Headline Dance Center'
					loading='eager'
				/>
				<span className='s-hero__scrim' aria-hidden='true' />
				<div className='s-hero__in'>
					<h1 className='brand-heading'>Summer at Color Your Voice</h1>
					<p>
						Four group workshops for kids and teens. Every workshop is open to
						all families — current students and newcomers alike. No experience
						needed, just curiosity.
					</p>
					<span className='s-hero__loc'>
						<FontAwesomeIcon icon={faLocationDot} /> Workshops meet inside Headline
						Dance Center, Burnsville
					</span>
					<div className='s-hero__cta'>
						<a className='btn-cyv-teal' href='#workshops'>
							Register for a workshop
						</a>
						<Link className='btn-cyv-ghost' to='/contact'>
							Get started
						</Link>
					</div>
				</div>
			</header>

			{loading && (
				<div className='s-sec'>
					<Spinner animation='border' role='status' style={{ color: 'var(--cyv-teal-600)' }}>
						<span className='visually-hidden'>Loading…</span>
					</Spinner>
				</div>
			)}

			{!loading && error && (
				<div className='s-sec'>
					<p>
						We had trouble loading this page. Please refresh, or call us at
						(952) 283-0560.
					</p>
				</div>
			)}

			{/* ---- Section 1 · Group workshops (cream cards, themed accent bars) ---- */}
			{!loading && !error && workshops.length > 0 && (
				<section className='d1-workshops' id='workshops'>
					<div className='s-sec'>
						<div className='s-sec__head'>
							<div>
								<span className='eyebrow'>
									{workshops.length} workshops · {months.join('–') || 'summer'}
								</span>
								<h2 className='s-sec__title'>Group workshops</h2>
								<p className='s-sec__lead'>
									Grow together. Register and pay online — spots are
									confirmed once payment is complete.
								</p>
							</div>
							<div className='s-legend'>
								<span><i style={{ background: 'var(--cyv-teal-600)' }} /> Open</span>
								<span><i style={{ background: '#e08a52' }} /> Nearly full</span>
							</div>
						</div>

						{/* Month jump-chips — reuse the existing month-filter logic. */}
						{months.length > 1 && (
							<div className='jump' role='group' aria-label='Filter workshops by month'>
								<button
									type='button'
									className={monthFilter === 'all' ? 'is-on' : ''}
									onClick={() => setMonthFilter('all')}>
									All {workshops.length}
								</button>
								{months.map((m) => (
									<button
										key={m}
										type='button'
										className={monthFilter === m ? 'is-on' : ''}
										onClick={() => setMonthFilter(m)}>
										{m}
									</button>
								))}
							</div>
						)}

						<div className='wk-grid'>
							{visible.map((w) => {
								const pill = seatPill(w);
								const monthChip = `${shortMonth(w.startDate)}${
									weekdays(w.dateLabel) ? ` · ${weekdays(w.dateLabel)}` : ''
								}`;
								return (
									<Link
										key={w.id}
										to={`/summer/${w.slug}${previewSuffix}`}
										className='cyv-card wk'
										style={{ '--accent': accentFor(w.heroTheme) }}
										aria-label={`View ${w.title}, ${w.dateLabel}`}>
										<span className='accent-bar' />
										<div className='wk__top'>
											<span className='s-month'>{monthChip}</span>
											<span className={`s-pill s-pill--${pill.tone}`}>
												<span className='s-pill__dot' />
												{pill.label}
											</span>
										</div>
										<h3 className='wk__title'>{w.title}</h3>
										<div className='wk__by'>{byline(w.instructors)}</div>
										<div className='wk__meta'>
											<div>
												<FontAwesomeIcon icon={faCalendarDay} />
												<span>{w.dateLabel}</span>
											</div>
											<div>
												<FontAwesomeIcon icon={faClock} />
												<span>
													{timeLines(w).map((t, i) => (
														<React.Fragment key={i}>
															{i > 0 && <br />}
															{t}
														</React.Fragment>
													))}
												</span>
											</div>
											<div>
												<FontAwesomeIcon icon={faUserGroup} />
												<span>{capLine(w)}</span>
											</div>
										</div>
										<div className='wk__foot'>
											<span className='wk__price'>{w.priceLabel}</span>
											<span className='wk__cta'>
												{pill.tone === 'full' ? 'Join waitlist' : 'Register'}{' '}
												<FontAwesomeIcon icon={faArrowRight} />
											</span>
										</div>
									</Link>
								);
							})}
						</div>
					</div>
				</section>
			)}

			{/* Section 2 held a private-lesson-packs teaser linking to the /lessons buy
			    flow. Removed 2026-08-18 with the packs themselves: a pack bought now has
			    nowhere to land between the summer run ending Aug 21 and the school year
			    starting Aug 31, and CYV does not sell packs during the school year at all
			    (core.md → Lessons). Workshops below are untouched and still selling. */}

			{/* ---- Policy / registration strip (page content; site footer renders below) ---- */}
			<footer className='s-policy'>
				<div className='s-policy__in'>
					<div className='s-policy__item'>
						<FontAwesomeIcon icon={faCircleCheck} />
						<div>
							<h5>Register online</h5>
							<p>
								Workshops register right here — self-serve and secure. Spots
								confirm once payment is complete.
							</p>
						</div>
					</div>
					<div className='s-policy__item'>
						<FontAwesomeIcon icon={faArrowRotateLeft} />
						<div>
							<h5>Flexible refunds</h5>
							<p>
								Full refund 14+ days before start, or within 48 hours of
								registering — whichever is later.
							</p>
						</div>
					</div>
					<div className='s-policy__item'>
						<FontAwesomeIcon icon={faCircleQuestion} />
						<div>
							<h5>Questions first?</h5>
							<p>
								Email sing@coloryourvoice.com or call (952) 283-0560 — we'll help
								you find the right fit.
							</p>
						</div>
					</div>
				</div>
			</footer>
		</main>
	);
};

export default SummerWorkshops;
